import React, { useEffect, useState } from "react";
import { Table, Button, Space, Modal, Form, Input, Card, Select } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const ProductCategoryManagement = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchCompanies();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get("super-admin/product-categories");
      setCategories(response.data);
    } catch (error) {
      toast.error("Failed to fetch product categories");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get("super-admin/companies");
      setCompanies(response.data);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      await axios.delete(`super-admin/product-categories/${record.id}`);
      toast.success("Product category deleted successfully");
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete product category");
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCategory) {
        await axios.put(`super-admin/product-categories/${editingCategory.id}`, values);
        toast.success("Product category updated successfully");
      } else {
        await axios.post("super-admin/product-categories", values);
        toast.success("Product category created successfully");
      }
      setModalVisible(false);
      form.resetFields();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save product category");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Company",
      dataIndex: ["company", "company_name"],
      key: "company",
      render: (text) => text || "N/A",
    },
    {
      title: "Products Count",
      dataIndex: ["_count", "product"],
      key: "productCount",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate("/super-admin/dashboard")}
        style={{ marginBottom: "16px" }}
      >
        Back to Dashboard
      </Button>
      <Card
        title="Product Category Management"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Product Category
          </Button>
        }
      >
        <Table
          dataSource={categories}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingCategory ? "Edit Product Category" : "Create Product Category"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: "Please input category name!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="company_id"
            label="Company"
            rules={[{ required: true, message: "Please select company!" }]}
          >
            <Select placeholder="Select Company" allowClear>
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.company_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductCategoryManagement;
